/**
 * Cart Drawer - Bundle Product Management
 * Handles bundle product display, discounts, and cart calculations in the cart drawer
 * 
 * Required data attributes on the data container:
 * - data-section-id: section ID
 * - data-cart-items: JSON of cart items
 * - data-has-discount-codes: boolean
 * - data-is-b2b: boolean (B2B ex-VAT pricing)
 */

(function() {
  // Get data container
  const dataContainer = document.querySelector('[data-cart-drawer-bundle-data]');
  
  // Get section ID and initial cart data
  const sectionId = dataContainer?.dataset.sectionId || '';
  const hasDiscountCodes = dataContainer?.dataset.hasDiscountCodes === 'true';
  const isB2B = dataContainer?.dataset.isB2b === 'true';
  
  // Parse initial cart items
  let initialCartItems = [];
  try {
    initialCartItems = JSON.parse(dataContainer?.dataset.cartItems || '[]');
  } catch (e) {
    initialCartItems = [];
  }


  const nokFormatter = new Intl.NumberFormat('no-NO', {
    style: 'currency',
    currency: 'NOK'
  });
  
  // Execution lock to prevent multiple simultaneous initializations
  let isInitializing = false;
  let isInitialized = false;
  
  // Cache DOM queries
  const domCache = {
    cartDrawer: null,
    cartItemsContainer: null,
    getCartDrawer() {
      return this.cartDrawer || (this.cartDrawer = document.querySelector('cart-drawer'));
    },
    getCartItemsContainer() {
      return this.cartItemsContainer || (this.cartItemsContainer = document.querySelector(`#CartDrawerBody-${sectionId}`));
    },
    clear() {
      this.cartDrawer = null;
      this.cartItemsContainer = null;
    }
  };
  
  // Normalize bundle_products_data entry to a numeric variant id
  // Supports ID-only arrays ([123,456]) and legacy full objects ([{id:123,...}])
  function getBundleComponentId(entry) {
    if (entry == null) return null;
    if (typeof entry === 'number') return entry;
    if (typeof entry === 'string') {
      const asNum = parseInt(entry, 10);
      return asNum > 0 ? asNum : null;
    }
    if (typeof entry === 'object' && entry.id != null) {
      return getBundleComponentId(entry.id);
    }
    return null;
  }

  // Look up a price from productsData when legacy full objects are present
  function getPriceFromProductsData(productsData, variantId) {
    if (!Array.isArray(productsData)) return null;
    const match = productsData.find((entry) => {
      if (entry && typeof entry === 'object' && entry.price != null) {
        return getBundleComponentId(entry) == variantId;
      }
      return false;
    });
    return match && match.price != null ? match.price : null;
  }

  // Function to get bundle data from cart item properties
  function getBundleDataFromItem(item) {
    if (!item.properties || !item.properties._is_bundle) {
      return null;
    }

    let productsData = null;
    if (item.properties._bundle_products_data) {
      try {
        productsData = JSON.parse(item.properties._bundle_products_data);
      } catch (e) {
        // Silent fail
      }
    }

    return {
      variantId: item.properties._variant_id,
      discountValue: item.properties._discount_value,
      bundleQuantity: parseInt(item.properties._bundle_quantity) || 1,
      userQuantity: parseInt(item.properties._user_quantity) || 1,
      isBundle: item.properties._is_bundle === 'true',
      productsData: productsData,
      productVariantId: item.properties._product_id
    };
  }

  // Function to group products by bundle ID
  function groupProductsByBundleVariant() {
    const cartItems = window.currentCartItems || initialCartItems;
    const bundleGroups = {};

    cartItems.forEach((item, index) => {
      if (item.properties && item.properties._is_bundle && item.properties._bundle_id) {
        const bundleId = item.properties._bundle_id;
        if (!bundleGroups[bundleId]) {
          bundleGroups[bundleId] = [];
        }
        bundleGroups[bundleId].push({
          index: index,
          item: item,
          bundleData: getBundleDataFromItem(item)
        });
      }
    });

    return bundleGroups;
  }

  // Function to validate if products are actually bundled
  function validateBundleGroup(group) {
    if (group.length < 2) {
      return false; // Need at least 2 products for a bundle
    }

    // Get the first item's data as reference
    const firstItem = group[0];
    const referenceProductsData = firstItem.bundleData.productsData;
    const referenceVariantId = firstItem.item.properties._variant_id;

    if (!referenceProductsData || !Array.isArray(referenceProductsData)) {
      return false;
    }

    // Check if all items in the group have the same bundle variant ID (bundle product ID)
    const hasSameVariantId = group.every(item => {
      return item.item.properties._variant_id === referenceVariantId;
    });

    if (!hasSameVariantId) {
      return false;
    }

    // Get the expected component variant IDs from the bundle products data
    // Supports ID-only ([123]) and legacy full objects ([{id:123}])
    const expectedVariantIds = referenceProductsData
      .map((entry) => String(getBundleComponentId(entry)))
      .filter((id) => id && id !== 'null')
      .sort();

    // Get the actual component variant IDs in the current group
    const actualVariantIds = group
      .map((item) => String(item.bundleData.productVariantId))
      .sort();

    // Check if we have ALL the required components (not just some of them)
    const hasAllComponents = expectedVariantIds.every(expectedId =>
      actualVariantIds.includes(expectedId)
    );

    const isValidBundle = hasAllComponents && actualVariantIds.length === expectedVariantIds.length;

    return isValidBundle;
  }

  // Resolve compare-at (original) unit price vs Cart Transform final price.
  // Cart Transform often leaves original_price == final_price; fall back to
  // reversing _discount_value so the UI can still show a price comparison.
  function getCompareUnitPrice(item) {
    const finalPrice = item.final_price != null ? item.final_price : item.price;
    let comparePrice = item.original_price != null ? item.original_price : finalPrice;

    if (comparePrice > finalPrice) {
      return comparePrice;
    }

    const discountPercent = parseFloat(item.properties && item.properties._discount_value) || 0;
    const isBundle = item.properties && (item.properties._is_bundle === 'true' || item.properties._is_bundle === true);
    if (isBundle && discountPercent > 0 && discountPercent < 100) {
      return Math.round(finalPrice / (1 - discountPercent / 100));
    }

    return comparePrice;
  }

  function getCompareLinePrice(item) {
    return getCompareUnitPrice(item) * (item.quantity || 1);
  }

  function renderPriceComparison(priceElement, comparePrice, finalPrice) {
    if (!priceElement) return;
    priceElement.innerHTML = '';

    const formattedFinal = nokFormatter.format(finalPrice / 100);
    const regularPriceSpan = document.createElement('span');
    regularPriceSpan.className = 'price__regular font-body-bolder';
    regularPriceSpan.textContent = formattedFinal;
    priceElement.appendChild(regularPriceSpan);

    if (comparePrice > finalPrice) {
      const formattedCompare = nokFormatter.format(comparePrice / 100);
      const salePriceSpan = document.createElement('s');
      salePriceSpan.className = 'price__sale font-body text-subtext';
      salePriceSpan.textContent = formattedCompare;
      priceElement.appendChild(salePriceSpan);
      priceElement.classList.add('price--on-sale');
    } else {
      priceElement.classList.remove('price--on-sale');
    }
  }

  // Sync bundle line UI with Cart Transform prices + original compare prices
  function applyBundleDiscounts() {
    const bundleGroups = groupProductsByBundleVariant();
    const cartItems = window.currentCartItems || initialCartItems;

    Object.keys(bundleGroups).forEach(bundleId => {
      const group = bundleGroups[bundleId];
      const isValidBundle = validateBundleGroup(group);

      group.forEach(({ index, item }) => {
        const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
        if (!cartItemElement) return;

        applyPriceComparisonDisplay(cartItemElement, item);
        if (isValidBundle) {
          toggleDiscountTags(cartItemElement, true);
        } else {
          removeBundleIndicator(cartItemElement);
          toggleDiscountTags(cartItemElement, false);
        }
      });
    });

    cartItems.forEach((item, index) => {
      if (!item.properties || !item.properties._is_bundle) {
        const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
        if (cartItemElement) {
          applyPriceComparisonDisplay(cartItemElement, item);
          removeBundleIndicator(cartItemElement);
          toggleDiscountTags(cartItemElement, false);
        }
      }
    });
  }

  function applyPriceComparisonDisplay(cartItemElement, item) {
    // Liquid section prices are authoritative for per-product VAT.
    if (isB2B) return;
    if (!cartItemElement) return;
    const comparePrice = getCompareLinePrice(item);
    const finalPrice = item.final_line_price != null
      ? item.final_line_price
      : (item.final_price != null ? item.final_price : item.price) * (item.quantity || 1);
    const priceElements = cartItemElement.querySelectorAll('.price');
    priceElements.forEach((priceElement) => {
      renderPriceComparison(priceElement, comparePrice, finalPrice);
    });
  }

  // Function to reset price display to Shopify original (pre-discount) price
  function resetToOriginalPrice(cartItemElement, item) {
    applyPriceComparisonDisplay(cartItemElement, item);
  }

  // Kept for compatibility; Cart Transform owns discount math — display Shopify prices
  function applyBundleDiscountToCartItem(itemIndex, item, bundleData) {
    const cartItemElement = document.querySelector(`#CartDrawer-Item-${itemIndex + 1}`);
    applyPriceComparisonDisplay(cartItemElement, item);
  }

  // Function to remove bundle indicator
  function removeBundleIndicator(cartItemElement) {
    if (!cartItemElement) return;
    const bundleIndicator = cartItemElement.querySelector('.bundle-indicator');
    if (bundleIndicator) {
      bundleIndicator.remove();
    }
  }

  // Function to toggle discount tag visibility based on bundle validity
  function toggleDiscountTags(cartItemElement, isValidBundle) {
    if (!cartItemElement) return;
    const discountsList = cartItemElement.querySelector('.cart-item__discounts');
    if (discountsList) {
      if (isValidBundle) {
        // Valid bundle - hide discount tags
        discountsList.style.display = 'none';
      } else {
        // Invalid bundle - show discount tags
        discountsList.style.display = '';
      }
    }
  }

  // Function to apply automatic discount display (for products with Shopify automatic discounts)
  function applyAutomaticDiscountDisplay(cartItemElement, item) {
    applyPriceComparisonDisplay(cartItemElement, item);
  }

  // Function to update bundle price display (shows both original and discounted prices)
  function updateBundlePriceDisplay(cartItemElement, originalPrice, discountedPrice, discountPercent) {
    if (!cartItemElement) return;
    const priceElements = cartItemElement.querySelectorAll('.price');
    priceElements.forEach((priceElement) => {
      renderPriceComparison(priceElement, originalPrice, discountedPrice);
    });
  }

  // Function to update quantity controls based on bundle validity
  function updateQuantityControls() {
    const cartItems = window.currentCartItems || initialCartItems;
    const bundleGroups = groupProductsByBundleVariant();

    cartItems.forEach((item, index) => {
      const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
      if (!cartItemElement) return;

      const quantityInput = cartItemElement.querySelector('input[name="updates[]"]');
      const minusButton = cartItemElement.querySelector('button[name="minus"]');
      const plusButton = cartItemElement.querySelector('button[name="plus"]');
      const quantityWrapper = cartItemElement.querySelector('.quantity');

      if (!quantityInput || !quantityWrapper) return;

      // Check if this is a bundle product
      if (item.properties && item.properties._is_bundle && item.properties._bundle_id) {
        const bundleId = item.properties._bundle_id;
        const bundleGroup = bundleGroups[bundleId];
        const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

        if (isValidBundle) {
          // Valid bundle - disable controls
          quantityInput.disabled = true;
          quantityInput.readOnly = true;
          if (minusButton) minusButton.disabled = true;
          if (plusButton) plusButton.disabled = true;
          quantityWrapper.classList.add('quantity--disabled');
          if (minusButton) minusButton.classList.add('disabled');
          if (plusButton) plusButton.classList.add('disabled');
          quantityInput.classList.add('disabled');
        } else {
          // Invalid bundle - enable controls
          quantityInput.disabled = false;
          quantityInput.readOnly = false;
          if (minusButton) minusButton.disabled = false;
          if (plusButton) plusButton.disabled = false;
          quantityWrapper.classList.remove('quantity--disabled');
          if (minusButton) minusButton.classList.remove('disabled');
          if (plusButton) plusButton.classList.remove('disabled');
          quantityInput.classList.remove('disabled');
        }
      } else {
        // Regular product - ensure controls are enabled
        quantityInput.disabled = false;
        quantityInput.readOnly = false;
        if (minusButton) minusButton.disabled = false;
        if (plusButton) plusButton.disabled = false;
        quantityWrapper.classList.remove('quantity--disabled');
        if (minusButton) minusButton.classList.remove('disabled');
        if (plusButton) plusButton.classList.remove('disabled');
        quantityInput.classList.remove('disabled');
      }
    });
  }

  // Function to update cart drawer subtotal with original vs discounted comparison
  function updateCartDrawerSubtotal() {
    // AJAX sections already contain mixed-rate ex-VAT subtotals.
    if (isB2B) return;
    if (hasDiscountCodes) {
      return;
    }

    const cartData = window.currentCartData;
    const cartItems = window.currentCartItems || initialCartItems;

    let discountedTotal = 0;
    let originalTotal = 0;

    if (cartData && cartData.total_price != null) {
      discountedTotal = cartData.total_price;
    }

    cartItems.forEach((item) => {
      const finalLine = item.final_line_price != null ? item.final_line_price : item.original_line_price;
      if (!(cartData && cartData.total_price != null)) {
        discountedTotal += finalLine;
      }
      originalTotal += getCompareLinePrice(item);
    });

    const subtotalElement = document.getElementById('cart-drawer-subtotal-value');
    if (!subtotalElement) return;

    if (originalTotal > discountedTotal) {
      subtotalElement.innerHTML =
        `<span class="price__regular">${nokFormatter.format(discountedTotal / 100)}</span>` +
        `<s class="price__sale font-body text-subtext">${nokFormatter.format(originalTotal / 100)}</s>`;
      subtotalElement.classList.add('price--on-sale');
    } else {
      subtotalElement.textContent = nokFormatter.format(discountedTotal / 100);
      subtotalElement.classList.remove('price--on-sale');
    }
  }

  // Function to update bundle product display
  function updateBundleProductDisplay() {
    applyBundleDiscounts();
    updateQuantityControls();
    updateCartDrawerSubtotal();
  }

  // Cart mutations are authoritative; older asynchronous reads must not repaint them.
  let reinitializeTimeout = null;
  let cartStateVersion = 0;

  function invalidateBundleRead() {
    cartStateVersion += 1;
    if (reinitializeTimeout) clearTimeout(reinitializeTimeout);
    reinitializeTimeout = null;
  }

  function acceptCartUpdate(event) {
    const cart = event?.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) {
      reinitializeBundleDisplay();
      return;
    }
    invalidateBundleRead();
    window.currentCartData = cart;
    window.currentCartItems = cart.items;
    // Missing bundled sections are refreshed asynchronously by cart.js.
    // Wait for its render-complete signal before addressing positional rows.
    if (typeof cart.sections?.[sectionId] === 'string' && cart.sections[sectionId]) {
      updateBundleProductDisplay();
      isInitialized = true;
    }
  }

  function reinitializeBundleDisplay() {
    invalidateBundleRead();
    const version = cartStateVersion;
    reinitializeTimeout = setTimeout(async () => {
      reinitializeTimeout = null;
      try {
        const cart = await getCurrentCartData();
        if (version !== cartStateVersion || !cart || !Array.isArray(cart.items)) return;
        window.currentCartData = cart;
        window.currentCartItems = cart.items;
        requestAnimationFrame(() => {
          if (version !== cartStateVersion) return;
          updateBundleProductDisplay();
          isInitialized = true;
        });
      } catch (error) {
        if (Shopify?.designMode) console.error('Error reinitializing bundle display:', error);
      }
    }, 150);
  }

  // Function to get current cart data - uses cached version from cart.js if available
  async function getCurrentCartData() {
    // Use cached fetchCartData from cart.js if available, otherwise fallback
    if (typeof window.fetchCartData === 'function') {
      try {
        return await window.fetchCartData(true);
      } catch (error) {
        if (Shopify?.designMode) {
          console.error('Error fetching cart data:', error);
        }
        return null;
      }
    }
    
    // Fallback if cart.js cache not available
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      if (Shopify?.designMode) {
        console.error('Error fetching cart data:', error);
      }
      return null;
    }
  }

  // Function to force refresh cart drawer content
  async function forceRefreshCartDrawer() {
    try {
      // Get the cart drawer section ID
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerSectionId = cartDrawer ? cartDrawer.getAttribute('data-section-id') : null;

      if (cartDrawerSectionId) {
        // Fetch fresh cart drawer content
        const response = await fetch(`/?section_id=${cartDrawerSectionId}`);
        const responseText = await response.text();

        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(responseText, 'text/html');

        // Update cart drawer body
        const cartDrawerBody = document.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);
        const newCartDrawerBody = parsedHTML.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);

        if (cartDrawerBody && newCartDrawerBody) {
          cartDrawerBody.innerHTML = newCartDrawerBody.innerHTML;
        }

        // Update cart drawer footer
        const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);
        const newCartDrawerFooter = parsedHTML.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);

        if (cartDrawerFooter && newCartDrawerFooter) {
          cartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
        }

        // Update cart drawer empty state
        const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);
        const newCartDrawerEmpty = parsedHTML.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);

        if (cartDrawerEmpty && newCartDrawerEmpty) {
          cartDrawerEmpty.innerHTML = newCartDrawerEmpty.innerHTML;
        }
      }

      // Get fresh cart data and update bundle display
      const currentCartData = await getCurrentCartData();
      if (currentCartData && currentCartData.items) {
        window.currentCartData = currentCartData;
        window.currentCartItems = currentCartData.items;

        // Show/hide cart drawer sections based on cart state
        const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
        const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
        const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);

        if (currentCartData.item_count > 0) {
          if (cartDrawerBody) cartDrawerBody.classList.remove('hidden');
          if (cartDrawerFooter) cartDrawerFooter.classList.remove('hidden');
          if (cartDrawerEmpty) cartDrawerEmpty.classList.add('hidden');
        } else {
          if (cartDrawerBody) cartDrawerBody.classList.add('hidden');
          if (cartDrawerFooter) cartDrawerFooter.classList.add('hidden');
          if (cartDrawerEmpty) cartDrawerEmpty.classList.remove('hidden');
        }
      }

      // Update bundle display with fresh data
      setTimeout(() => {
        updateBundleProductDisplay();
      }, 100);

    } catch (error) {
      // Silent fail
    }
  }

  // Function to get original product price from product data
  function getOriginalProductPrice(item, bundleData) {
    // Legacy full objects may include catalog price at add-to-cart time
    if (bundleData.productsData && Array.isArray(bundleData.productsData)) {
      const productPrice = getPriceFromProductsData(bundleData.productsData, item.variant_id);
      if (productPrice != null) {
        return productPrice;
      }
    }

    // ID-only (or missing price): cart original_price is already the pre-discount
    // variant price. Do NOT reverse _discount_value — that inflates the compare
    // price and makes the bundle discount look applied twice.
    return item.original_price;
  }

  // Initialize bundle logic ONLY when cart drawer opens (not on page load)
  function initializeBundleCart() {
    // Prevent multiple initializations
    if (isInitializing || isInitialized) {
      return;
    }
    
    isInitializing = true;
    const version = cartStateVersion;
    
    getCurrentCartData().then(currentCartData => {
      if (version !== cartStateVersion) {
        isInitializing = false;
        return;
      }
      if (currentCartData && currentCartData.items) {
        window.currentCartData = currentCartData;
        window.currentCartItems = currentCartData.items;
      }
      updateBundleProductDisplay();
      isInitialized = true;
      isInitializing = false;
    }).catch(error => {
      if (Shopify?.designMode) {
        console.error('Error initializing bundle cart:', error);
      }
      isInitializing = false;
    });
  }

  // Listen for cart drawer opening to initialize/refresh bundle logic
  const cartDrawer = domCache.getCartDrawer();
  if (cartDrawer) {
    // Merged MutationObserver with debounce for drawer opening
    let drawerOpenTimeout = null;
    const mergedObserver = new MutationObserver((mutations) => {
      // Clear existing timeout
      if (drawerOpenTimeout) {
        clearTimeout(drawerOpenTimeout);
      }
      
      // Debounce observer callbacks
      drawerOpenTimeout = setTimeout(() => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
            const drawer = mutation.target;
            if (!drawer.hasAttribute('hidden')) {
              // Drawer opened - initialize bundle logic
              initializeBundleCart();
            }
          }
        });
      }, 150);
    });

    mergedObserver.observe(cartDrawer, { 
      attributes: true,
      attributeFilter: ['hidden']
    });

    // Also listen for the drawer's show method being called
    const originalShow = cartDrawer.show;
    if (originalShow) {
      cartDrawer.show = function(...args) {
        const result = originalShow.apply(this, args);
        // Initialize bundle logic when drawer is shown
        initializeBundleCart();
        return result;
      };
    }
  }

  // Listen for cart updates and reinitialize bundle display
  document.addEventListener('cart:updated', acceptCartUpdate);
  document.addEventListener('cart:rendered', reinitializeBundleDisplay);
  document.addEventListener('cart:refresh', invalidateBundleRead);
  document.addEventListener('cart:change', reinitializeBundleDisplay);

  // Also listen for product added events to refresh bundle display
  document.addEventListener('product-ajax:added', function(event) {
    setTimeout(() => {
      reinitializeBundleDisplay();
    }, 100);
  });

  // Listen for quantity changes
  document.addEventListener('change', function(event) {
    if (event.target.matches('input[name="updates[]"]')) {
      // Check if this is a bundle product
      const cartItem = event.target.closest('[id^="CartDrawer-Item-"]');
      if (cartItem) {
        const cartItemIndex = cartItem.id.replace('CartDrawer-Item-', '');
        const cartItems = window.currentCartItems || initialCartItems;
        const item = cartItems[parseInt(cartItemIndex) - 1];

        if (item && item.properties && item.properties._is_bundle && item.properties._bundle_id) {
          // Check if this bundle group is still valid
          const bundleGroups = groupProductsByBundleVariant();
          const bundleId = item.properties._bundle_id;
          const bundleGroup = bundleGroups[bundleId];
          const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

          if (isValidBundle) {
            // Reset to original quantity
            event.target.value = item.quantity;
            event.preventDefault();
            return false;
          }
        }
      }

      // The theme debounces the mutation; wait for its cart:updated response.
      invalidateBundleRead();
    }
  });

  // Listen for item removals and quantity button clicks
  document.addEventListener('click', function(event) {
    if (event.target.closest('[is="cart-remove-item"]') || event.target.closest('[is="gift-wrap-remove-item"]')) {
      // Do not fetch the pre-removal cart or address new rows with old indexes.
      invalidateBundleRead();
    }

    // Prevent quantity button clicks for bundle products
    if (event.target.matches('.quantity__button')) {
      const cartItem = event.target.closest('[id^="CartDrawer-Item-"]');
      if (cartItem) {
        const cartItemIndex = cartItem.id.replace('CartDrawer-Item-', '');
        const cartItems = window.currentCartItems || initialCartItems;
        const item = cartItems[parseInt(cartItemIndex) - 1];

        if (item && item.properties && item.properties._is_bundle && item.properties._bundle_id) {
          // Check if this bundle group is still valid
          const bundleGroups = groupProductsByBundleVariant();
          const bundleId = item.properties._bundle_id;
          const bundleGroup = bundleGroups[bundleId];
          const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

          if (isValidBundle) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        }
      }
    }
  });

  // Make functions globally available
  window.getBundleDataFromItem = getBundleDataFromItem;
  window.updateBundleProductDisplay = updateBundleProductDisplay;
  window.groupProductsByBundleVariant = groupProductsByBundleVariant;
  window.validateBundleGroup = validateBundleGroup;
  window.applyBundleDiscounts = applyBundleDiscounts;
  window.updateQuantityControls = updateQuantityControls;
  window.updateCartDrawerSubtotal = updateCartDrawerSubtotal;
  window.reinitializeBundleDisplay = reinitializeBundleDisplay;
  window.forceRefreshCartDrawer = forceRefreshCartDrawer;
  window.toggleDiscountTags = toggleDiscountTags;
  window.applyAutomaticDiscountDisplay = applyAutomaticDiscountDisplay;

  // Manual trigger function
  window.forceBundleUpdate = function() {
    reinitializeBundleDisplay();
  };

  // Add manual trigger for subtotal update
  window.forceSubtotalUpdate = function() {
    updateCartDrawerSubtotal();
  };
})();